import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";

import { zodResolver } from "@hookform/resolvers/zod";

import {
    getForms,
    getQuestionsByForm,
    getConfigurableTexts,
    updateConfigurableTexts,
    updateQuestionsBulk,
} from "@/api/forms-questions/forms-questions-services";
import { updateCompany } from "@/api/company/company-services";
import { KanbanBoard } from "@/components/drag-drop/drag-drop";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
    AccessibilityHelp,
    Alignment,
    AutoImage,
    AutoLink,
    Autoformat,
    Autosave,
    BlockQuote,
    Bold,
    ClassicEditor,
    CloudServices,
    Code,
    CodeBlock,
    Essentials,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    HorizontalLine,
    HtmlComment,
    HtmlEmbed,
    Image,
    ImageBlock,
    ImageCaption,
    ImageInline,
    ImageInsert,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    LinkImage,
    List,
    ListProperties,
    Paragraph,
    PasteFromOffice,
    PictureEditing,
    SelectAll,
    ShowBlocks,
    SourceEditing,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextTransformation,
    Undo,
    Base64UploadAdapter
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Toaster, toast } from "sonner";
import { z } from "zod";
import { useStore } from "@/store";

const formSchema = z.object({
    headerText: z.string(),
    footerText: z.string(),
});

export const normalizeConfigurableTextHtml = (html = "") => {
    if (typeof html !== "string" || html.trim() === "") {
        return typeof html === "string" ? html : "";
    }

    if (
        typeof document === "undefined" ||
        typeof NodeFilter === "undefined" ||
        typeof HTMLUnknownElement === "undefined"
    ) {
        return html;
    }

    const template = document.createElement("template");
    template.innerHTML = html;

    const walker = document.createTreeWalker(
        template.content,
        NodeFilter.SHOW_ELEMENT
    );
    const unknownElements = [];

    let currentNode = walker.nextNode();

    while (currentNode) {
        if (currentNode instanceof HTMLUnknownElement) {
            unknownElements.push(currentNode);
        }
        currentNode = walker.nextNode();
    }

    unknownElements.forEach((element) => {
        const replacement = document.createElement("div");

        Array.from(element.attributes).forEach((attribute) => {
            replacement.setAttribute(attribute.name, attribute.value);
        });

        while (element.firstChild) {
            replacement.appendChild(element.firstChild);
        }

        element.replaceWith(replacement);
    });

    return template.innerHTML;
};

const ComplaintFormView = ({ type, formType, selectedLang }) => {
    const [questions, setQuestions] = useState([]);
    const [questionsVisibility, setQuestionsVisibility] = useState([]);
    const [configTexts, setConfigTexts] = useState([]);
    const [currConfigText, setCurrConfigText] = useState([]);
    const [formData, setFormData] = useState([]);
    const [allColumns, setAllColumns] = useState([]);
    const [headerEditorInstance, setHeaderEditorInstance] = useState(null);
    const [footerEditorInstance, setFooterEditorInstance] = useState(null);
    const { i18n } = useTranslation();
    const { t } = useTranslation();
    
    const companyData = useStore((state) => state.companyData);
    const getCompanyData = useStore((state) => state.getCompanyData);

    const getBaseColumns = () => {
        console.log("Ejecutando getBaseColumns()...");
        console.log("Traducción de actions:", t("headColumns.communications.actions"));
        console.log("Fallback de actions:", t("buttonTexts.actions"));
        
        const columns = [
            { 
                id: "id", 
                accessorKey: "id", 
                content: "Id",
                isBase: true
            },
            { 
                id: "identificationNumber", 
                accessorKey: "identificationNumber", 
                content: t("headColumns.communications.trackingNumber"),
                isBase: true
            },
            { 
                id: "type", 
                accessorKey: "type", 
                content: t("headColumns.communications.communicationType"),
                isBase: true
            },
            { 
                id: "date", 
                accessorKey: "date", 
                content: t("headColumns.communications.createDate"),
                isBase: true
            },
            { 
                id: "limitDate", 
                accessorKey: "limitDate", 
                content: t("headColumns.communications.limitDate"),
                isBase: true
            },
            { 
                id: "state", 
                accessorKey: "state", 
                content: t("headColumns.communications.state"),
                isBase: true
            },
            { 
                id: "risk", 
                accessorKey: "risk", 
                content: t("headColumns.communications.priority"),
                isBase: true
            },
            { 
                id: "source", 
                accessorKey: "source", 
                content: t("headColumns.communications.source"),
                isBase: true
            },
            { 
                id: "tags", 
                accessorKey: "tags", 
                content: t("headColumns.communications.tags"),
                isBase: true
            },
            { 
                id: "solveDate", 
                accessorKey: "solveDate", 
                content: t("headColumns.communications.solveDate"),
                isBase: true
            },
            { 
                id: "actions", 
                accessorKey: "actions", 
                content: t("headColumns.communications.actions") || t("buttonTexts.actions") || "Acciones",
                isBase: true
            }
        ];
        
        console.log("Columnas base generadas en getBaseColumns():", columns.length);
        console.log("Verificando columna actions:", columns.find(c => c.accessorKey === "actions"));
        
        return columns;
    };

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            headerText: "",
            footerText: "",
        },
    });
    const { setValue } = form;

    useEffect(() => {
        const currentConfigTexts = configTexts.find(
            (text) =>
                text.type.toLowerCase() === type.toLowerCase() &&
                (!text.form || text.form?.type?.toLowerCase() === formType.toLowerCase())
        );

        setCurrConfigText(currentConfigTexts);

        const headerText = normalizeConfigurableTextHtml(
            currentConfigTexts?.preTexts?.find(
                (currText) => currText.language === selectedLang
            )?.text || ""
        );

        const footerText = normalizeConfigurableTextHtml(
            currentConfigTexts?.postTexts?.find(
                (currText) => currText.language === selectedLang
            )?.text || ""
        );

        setValue("headerText", headerText);
        setValue("footerText", footerText);

        // Update editor instances directly if they exist
        if (headerEditorInstance) {
            headerEditorInstance.setData(headerText);
        }
        if (footerEditorInstance) {
            footerEditorInstance.setData(footerText);
        }
    }, [configTexts, type, formType, selectedLang, headerEditorInstance, footerEditorInstance, setValue]);

    useEffect(() => {
        const fetchConfigTexts = async () => {
            try {
                const configTexts = await getConfigurableTexts();
                setConfigTexts(configTexts);
            } catch (error) {
                console.error("Error fetching form questions:", error);
            }
        };
        fetchConfigTexts();
    }, []);

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                // Obtener los formularios
                const forms = await getForms();

                // Buscar el formulario basado en el `formType`
                const form = forms.find((f) => f.type === formType);

                // Obtener la configuración actual de columnas base desde el store
                const currentBaseColumns = companyData?.baseColumns || {};

                console.log("=== DEBUG INFO ===");
                console.log("Vista actual (type):", type);
                console.log("¿Es vista list?:", type.toLowerCase() === "list");

                if (form?.id) {
                    // Obtener las preguntas del formulario
                    const data = await getQuestionsByForm(form.id);
                    setFormData(data);

                    // Formatear las preguntas dinámicas para el KanbanBoard
                    const dynamicQuestions = data
                        .filter(
                            (each) => each.active === true
                        )
                        .map((each) => ({
                            content:
                                each.texts.filter((lang) => lang.language === selectedLang)[0]
                                    ?.text || each.alias,
                            columnId: each.visibility?.[type] ? true : false,
                            id: each.id,
                            accessorKey: each.alias,
                            isBase: false,
                            order: each.order || 999,
                            originalVisibility: each.visibility?.[type]
                        }));

                    console.log("Columnas dinámicas encontradas:", dynamicQuestions.length);

                    let combinedColumns = [...dynamicQuestions];

                    // Solo agregar columnas base si el tipo de vista es "list"
                    if (type.toLowerCase() === "list") {
                        console.log("Entrando en lógica de columnas base...");
                        
                        // Primero verificar qué devuelve getBaseColumns()
                        const baseColumnsRaw = getBaseColumns();
                        console.log("Columnas base crudas (getBaseColumns()):", baseColumnsRaw);
                        console.log("Cantidad de columnas base:", baseColumnsRaw.length);
                        
                        // Verificar específicamente si actions está incluida
                        const actionsColumn = baseColumnsRaw.find(col => col.accessorKey === "actions");
                        console.log("¿Columna actions encontrada en getBaseColumns()?", !!actionsColumn);
                        if (actionsColumn) {
                            console.log("Detalles de columna actions:", actionsColumn);
                        }

                        // Obtener las columnas base con su estado de visibilidad actual
                        const baseColumns = baseColumnsRaw.map(baseCol => ({
                            ...baseCol,
                            columnId: true, // Las columnas base siempre aparecen como visibles en vista list
                            order: currentBaseColumns[baseCol.accessorKey] || 999
                        }));

                        console.log("Columnas base procesadas:", baseColumns);
                        console.log("Cantidad de columnas base procesadas:", baseColumns.length);
                        console.log("Current base columns config:", currentBaseColumns);

                        // Verificar específicamente si actions sigue estando después del mapeo
                        const actionsColumnProcessed = baseColumns.find(col => col.accessorKey === "actions");
                        console.log("¿Columna actions después del mapeo?", !!actionsColumnProcessed);
                        if (actionsColumnProcessed) {
                            console.log("Detalles de columna actions procesada:", actionsColumnProcessed);
                        }

                        // Combinar columnas base y dinámicas
                        combinedColumns = [...baseColumns, ...dynamicQuestions];
                        console.log("Columnas combinadas total:", combinedColumns.length);
                        
                        // Verificar una vez más si actions está en el resultado final
                        const actionsInFinal = combinedColumns.find(col => col.accessorKey === "actions");
                        console.log("¿Columna actions en resultado final?", !!actionsInFinal);
                        if (actionsInFinal) {
                            console.log("Detalles de columna actions en resultado final:", actionsInFinal);
                        }
                    }

                    // Ordenar todas las columnas por orden
                    combinedColumns.sort((a, b) => (a.order || 999) - (b.order || 999));

                    console.log("Columnas finales a mostrar:", combinedColumns.length);
                    console.log("Lista completa de columnas:", combinedColumns.map(col => ({ 
                        accessorKey: col.accessorKey, 
                        content: col.content, 
                        isBase: col.isBase,
                        columnId: col.columnId,
                        order: col.order
                    })));

                    // Establecer las columnas combinadas
                    setAllColumns(combinedColumns);
                    setQuestions(combinedColumns);
                } else {
                    console.error(`No se encontró formulario para el tipo: ${formType}`);
                    
                    let combinedColumns = [];
                    
                    // Solo mostrar columnas base si el tipo de vista es "list"
                    if (type.toLowerCase() === "list") {
                        // Si no hay formulario, solo mostrar columnas base
                        const baseColumns = getBaseColumns().map(baseCol => ({
                            ...baseCol,
                            columnId: true, // Las columnas base siempre aparecen como visibles en vista list
                            order: currentBaseColumns[baseCol.accessorKey] || 999
                        }));
                        baseColumns.sort((a, b) => (a.order || 999) - (b.order || 999));
                        combinedColumns = baseColumns;
                    }

                    setAllColumns(combinedColumns);
                    setQuestions(combinedColumns);
                }
            } catch (error) {
                console.error("Error al cargar las preguntas del formulario:", error);
            }
        };
        fetchQuestions();
    }, [type, formType, selectedLang, companyData]);

    const questionToSend = (item) => {
        return {
            id: item.id,
            form_id: item.form?.id,
            texts: item.texts,
            placeholders: item.placeholders,
            descriptions: item.descriptions,
            type: item.type,
            position: item.position,
            alias: item.alias,
            visibility: item.visibility,
            depends_id: item.depends?.id || null,
            order: item.order
        };
    };

    function updateOrAddText(obj, section, language, text) {
        if (!obj[section]) {
            obj[section] = {};
        }

        obj[section][language] = text;
    }

    function mapObject(obj) {
        if (!obj) return {};
        return {
            type: obj.type || "",
            preTexts: obj.preTexts
                ? obj.preTexts.reduce((acc, text) => {
                    acc[text.language] = text.text;
                    return acc;
                }, {})
                : {},
            postTexts: obj.postTexts
                ? obj.postTexts.reduce((acc, text) => {
                    acc[text.language] = text.text;
                    return acc;
                }, {})
                : {},
            titles: obj.titles
                ? obj.titles.reduce((acc, text) => {
                    acc[text.language] = text.text;
                    return acc;
                }, {})
            : {},
        };
    }

    const hasQuestionConfigurationChanges = (columnsToPersist) => {
        if (!Array.isArray(columnsToPersist) || !Array.isArray(allColumns)) {
            return false;
        }

        if (columnsToPersist.length !== allColumns.length) {
            return true;
        }

        const getColumnKey = (column) =>
            column?.isBase ? `base:${column.accessorKey}` : `question:${column.id}`;

        for (let index = 0; index < columnsToPersist.length; index += 1) {
            const currentColumn = columnsToPersist[index];
            const initialColumn = allColumns[index];

            if (getColumnKey(currentColumn) !== getColumnKey(initialColumn)) {
                return true;
            }

            if (Boolean(currentColumn?.columnId) !== Boolean(initialColumn?.columnId)) {
                return true;
            }
        }

        return false;
    };

    const getCurrentConfigText = (texts) =>
        texts.find(
            (text) =>
                text.type.toLowerCase() === type.toLowerCase() &&
                (!text.form || text.form?.type?.toLowerCase() === formType.toLowerCase())
        );

    const wait = (ms) =>
        new Promise((resolve) => {
            setTimeout(resolve, ms);
        });

    const syncSourceEditingData = (editorInstance) => {
        const sourceEditingPlugin = editorInstance?.plugins?.get?.("SourceEditing");

        if (
            sourceEditingPlugin?.isSourceEditingMode &&
            typeof sourceEditingPlugin.updateEditorData === "function"
        ) {
            sourceEditingPlugin.updateEditorData();
        }
    };

    const getCurrentEditorData = (editorInstance, fallbackValue = "") => {
        syncSourceEditingData(editorInstance);

        if (typeof editorInstance?.getData === "function") {
            return editorInstance.getData();
        }

        return fallbackValue;
    };

    const shouldRetryConfigurableTextUpdate = (error) => {
        const status = error?.response?.status;
        return [409, 423, 429, 500, 502, 503].includes(status);
    };

    const updateConfigurableTextsWithRetry = async (id, dataToSend) => {
        const maxAttempts = 3;
        let attempt = 0;

        while (attempt < maxAttempts) {
            attempt += 1;
            try {
                return await updateConfigurableTexts(id, dataToSend);
            } catch (error) {
                const canRetry =
                    attempt < maxAttempts && shouldRetryConfigurableTextUpdate(error);
                if (!canRetry) {
                    throw error;
                }
                await wait(300 * attempt);
            }
        }
    };

    const onSubmit = async (values) => {
        const objectToSend = [];
        
        const baseColumnsOrder = {};
        const dynamicColumnsData = [];
        const columnsToPersist =
            questionsVisibility.length > 0 ? questionsVisibility : allColumns;
        const shouldPersistQuestionChanges =
            hasQuestionConfigurationChanges(columnsToPersist);

        columnsToPersist.forEach((column, index) => {
            if (column.isBase) {
                // Solo procesar columnas base si estamos en la vista "list"
                if (type.toLowerCase() === "list" && column.columnId === true) {
                    baseColumnsOrder[column.accessorKey] = index + 1;
                }
            } else {
                // Es una columna dinámica - procesar como antes
                const matchingData = formData.find(
                    (currentData) => currentData.id === column.id
                );

                if (matchingData) {
                    if (!matchingData.visibility) {
                        matchingData.visibility = {};
                    }
                    // Si la columna está visible (columnId === true), usar el tipo de vista, si no usar false
                    matchingData.visibility[type] = column.columnId === true ? (column.originalVisibility || type) : false;
                    // Establecer el order basado en la posición actual en el KanbanBoard
                    matchingData.order = index + 1;
                    objectToSend.push(questionToSend(matchingData));
                    dynamicColumnsData.push(matchingData);
                }
            }
        });

        try {
            let configTextToUpdate = currConfigText;

            if (!configTextToUpdate?.id) {
                configTextToUpdate = getCurrentConfigText(configTexts);
            }

            if (!configTextToUpdate?.id) {
                const latestConfigTexts = await getConfigurableTexts();
                setConfigTexts(latestConfigTexts);
                configTextToUpdate = getCurrentConfigText(latestConfigTexts);
            }

            if (!configTextToUpdate?.id) {
                throw new Error("No se encontró el texto configurable a actualizar");
            }

            const configTextToSend = mapObject(configTextToUpdate);
            const headerTextToSend = getCurrentEditorData(
                headerEditorInstance,
                values?.headerText || ""
            );
            const footerTextToSend = getCurrentEditorData(
                footerEditorInstance,
                values?.footerText || ""
            );

            updateOrAddText(
                configTextToSend,
                "preTexts",
                selectedLang,
                headerTextToSend
            );
            updateOrAddText(
                configTextToSend,
                "postTexts",
                selectedLang,
                footerTextToSend
            );

            if (shouldPersistQuestionChanges && objectToSend.length > 0) {
                await updateQuestionsBulk(objectToSend, type);
            }

            await updateConfigurableTextsWithRetry(
                configTextToUpdate.id,
                configTextToSend
            );

            // Refresh configurable texts to avoid stale data overwriting
            // previously saved languages when saving another language.
            const latestConfigTexts = await getConfigurableTexts();
            setConfigTexts(latestConfigTexts);

            if (
                shouldPersistQuestionChanges &&
                Object.keys(baseColumnsOrder).length > 0
            ) {
                const updatedCompanyData = {
                    ...companyData,
                    baseColumns: baseColumnsOrder
                };

                await updateCompany(updatedCompanyData);
                await getCompanyData();
            }

            toast.success(t("alertDialog.toast.updatedSuccess"));
            
            if (shouldPersistQuestionChanges) {
                setFormData(dynamicColumnsData);
            }
            
        } catch (error) {
            console.error("Error al guardar:", error);
            toast.error(t("alertDialog.toast.updateError") || "Error al actualizar");
        }
    };

    const editorConfig = {
        toolbar: {
            items: [
                "undo",
                "redo",
                "|",
                "sourceEditing",
                "showBlocks",
                "|",
                "heading",
                "|",
                "fontSize",
                "fontFamily",
                "fontColor",
                "fontBackgroundColor",
                "|",
                "bold",
                "italic",
                "code",
                "|",
                "horizontalLine",
                "link",
                "insertImage",
                "insertTable",
                "blockQuote",
                "codeBlock",
                "htmlEmbed",
                "|",
                "alignment",
                "|",
                "bulletedList",
                "numberedList",
                "outdent",
                "indent",
            ],
            shouldNotGroupWhenFull: true,
        },
        plugins: [
            AccessibilityHelp,
            Alignment,
            Autoformat,
            AutoImage,
            AutoLink,
            Autosave,
            Base64UploadAdapter,
            BlockQuote,
            Bold,
            CloudServices,
            Code,
            CodeBlock,
            Essentials,
            FontBackgroundColor,
            FontColor,
            FontFamily,
            FontSize,
            GeneralHtmlSupport,
            Heading,
            HorizontalLine,
            HtmlComment,
            HtmlEmbed,
            Image,
            ImageBlock,
            ImageCaption,
            ImageInline,
            ImageInsert,
            ImageInsertViaUrl,
            ImageResize,
            ImageStyle,
            ImageTextAlternative,
            ImageToolbar,
            ImageUpload,
            Indent,
            IndentBlock,
            Italic,
            Link,
            LinkImage,
            List,
            ListProperties,
            Paragraph,
            PasteFromOffice,
            PictureEditing,
            SelectAll,
            ShowBlocks,
            SourceEditing,
            Table,
            TableCaption,
            TableCellProperties,
            TableColumnResize,
            TableProperties,
            TableToolbar,
            TextTransformation,
            Undo,
        ],
        fontFamily: {
            supportAllValues: true,
        },
        fontSize: {
            options: [10, 12, 14, "default", 18, 20, 22],
            supportAllValues: true,
        },
        heading: {
            options: [
                {
                    model: "paragraph",
                    title: "Paragraph",
                    class: "ck-heading_paragraph",
                },
                {
                    model: "heading1",
                    view: "h1",
                    title: "Heading 1",
                    class: "ck-heading_heading1",
                },
                {
                    model: "heading2",
                    view: "h2",
                    title: "Heading 2",
                    class: "ck-heading_heading2",
                },
                {
                    model: "heading3",
                    view: "h3",
                    title: "Heading 3",
                    class: "ck-heading_heading3",
                },
                {
                    model: "heading4",
                    view: "h4",
                    title: "Heading 4",
                    class: "ck-heading_heading4",
                },
                {
                    model: "heading5",
                    view: "h5",
                    title: "Heading 5",
                    class: "ck-heading_heading5",
                },
                {
                    model: "heading6",
                    view: "h6",
                    title: "Heading 6",
                    class: "ck-heading_heading6",
                },
            ],
        },
        htmlSupport: {
            allow: [
                {
                    name: /^.*$/,
                    styles: true,
                    attributes: true,
                    classes: true,
                },
            ],
        },
        image: {
            toolbar: [
                "toggleImageCaption",
                "imageTextAlternative",
                "|",
                "imageStyle:inline",
                "imageStyle:wrapText",
                "imageStyle:breakText",
                "|",
                "resizeImage",
            ],
        },
        link: {
            addTargetToExternalLinks: true,
            defaultProtocol: "https://",
            decorators: {
                toggleDownloadable: {
                    mode: "manual",
                    label: "Downloadable",
                    attributes: {
                        download: "file",
                    },
                },
            },
        },
        list: {
            properties: {
                styles: true,
                startIndex: true,
                reversed: true,
            },
        },
        table: {
            contentToolbar: [
                "tableColumn",
                "tableRow",
                "mergeTableCells",
                "tableProperties",
                "tableCellProperties",
            ],
        },
    };

    return (
        <section className="w-full flex justify-center mt-5">
            <Toaster />
            <Card className="p-6 w-full">
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <FormField
                            control={form.control}
                            name="headerText"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("MainLayout.views.header")}</FormLabel>
                                    <FormControl>
                                        <div className="w-full max-w-full overflow-hidden">
                                            <CKEditor
                                                key={`header-${type}-${formType}-${selectedLang}-${currConfigText?.id}`}
                                                editor={ClassicEditor}
                                                onReady={(editor) => {
                                                    setHeaderEditorInstance(editor);
                                                }}
                                                onChange={(_event, editor) => {
                                                    const data = editor.getData();
                                                    field.onChange(data);
                                                }}
                                                config={editorConfig}
                                            />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <KanbanBoard
                            data={questions}
                            setData={setQuestionsVisibility}
                        />

                        <FormField
                            control={form.control}
                            name="footerText"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t("MainLayout.views.footer")}</FormLabel>
                                    <FormControl>
                                        <div className="w-full max-w-full overflow-hidden">
                                            <CKEditor
                                                key={`footer-${type}-${formType}-${selectedLang}-${currConfigText?.id}`}
                                                editor={ClassicEditor}
                                                onReady={(editor) => {
                                                    setFooterEditorInstance(editor);
                                                }}
                                                onChange={(_event, editor) => {
                                                    const data = editor.getData();
                                                    field.onChange(data);
                                                }}
                                                config={editorConfig}
                                            />
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Button
                            type="submit"
                            className="mt-4 bg-primary hover:bg-primary-600"
                        >
                            {t("MainLayout.views.update")}
                        </Button>
                    </form>
                </Form>
            </Card>
        </section>
    );
};
export default ComplaintFormView;
